/**
 * 皮肤管理相关 API
 * 用于用户端获取并响应管理端配置的前端功能主页面皮肤
 */

import axiosInstance from "../services/axiosConfig";
import { API_PATHS, STORAGE_KEYS } from "./config";
import Logger from "../utils/logger";

// 默认皮肤备选（默认极简科技风，移除节日装饰负担）
export const DEFAULT_FALLBACK_SKIN = {
  skin_key: "default",
  name: "默认极简科技风",
  description: "经典科技极简风格，专注核心创作体验",
  theme_color: "#1a73e8",
  is_active: 1,
  config: {
    badge: "经典模式",
    slogan: "AI 驱动的数学动画与交互课件生成平台",
    showLanterns: false,
    showRabbit: false,
    showFlowers: false,
    showParticles: true,
    accentColor: "#1a73e8",
  },
};

/**
 * 获取本地缓存的皮肤配置
 */
export const getCachedActiveSkin = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.ACTIVE_SKIN);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.skin_key === "string") {
        return parsed;
      }
    }
  } catch (e) {
    Logger.warn("读取本地皮肤缓存失败", e);
  }
  return DEFAULT_FALLBACK_SKIN;
};

/**
 * 从后端异步获取当前生效的皮肤
 */
export const getActiveSkin = async () => {
  try {
    const response = await axiosInstance.get(
      `${API_PATHS.BASE_API_URL}${API_PATHS.SKIN.GET_ACTIVE}`
    );
    if (response?.success && response.data?.skin_key) {
      try {
        localStorage.setItem(
          STORAGE_KEYS.ACTIVE_SKIN,
          JSON.stringify(response.data)
        );
      } catch (err) {
        Logger.warn("写入皮肤缓存失败", err);
      }
      return response.data;
    }
  } catch (error) {
    Logger.warn("获取服务端生效皮肤失败，回退使用本地缓存", error);
  }
  return getCachedActiveSkin();
};

/**
 * 获取用户今日抓玉兔领积分状态
 * @param {number|string} userId
 */
export const getRabbitCatchStatus = async (userId) => {
  if (!userId) return { success: false, data: null };
  try {
    const response = await axiosInstance.get(
      `${API_PATHS.BASE_API_URL}${API_PATHS.SKIN.RABBIT_STATUS}`,
      { params: { user_id: userId } }
    );
    return response;
  } catch (error) {
    Logger.warn("获取玉兔积分状态失败", error);
    return { success: false, message: error.message };
  }
};

/**
 * 用户鼠标捕捉玉兔领取积分（每次2分，每日上限10分）
 * @param {number|string} userId
 */
export const catchRabbit = async (userId) => {
  if (!userId) return { success: false, message: "请先登录" };
  try {
    const response = await axiosInstance.post(
      `${API_PATHS.BASE_API_URL}${API_PATHS.SKIN.RABBIT_CATCH}`,
      null,
      { params: { user_id: userId } }
    );
    return response;
  } catch (error) {
    Logger.warn("捕捉玉兔失败", error);
    return { success: false, message: error.message || "捕捉失败" };
  }
};

/**
 * 获取用户今日国庆献花领积分状态
 * @param {number|string} userId
 */
export const getFlowerTributeStatus = async (userId) => {
  if (!userId) return { success: false, data: null };
  try {
    const response = await axiosInstance.get(
      `${API_PATHS.BASE_API_URL}${API_PATHS.SKIN.FLOWER_STATUS}`,
      { params: { user_id: userId } }
    );
    return response;
  } catch (error) {
    Logger.warn("获取国庆献花积分状态失败", error);
    return { success: false, message: error.message };
  }
};

/**
 * 用户鼠标献礼鲜花为祖国献礼领取积分（每次2分，每日上限10分）
 * @param {number|string} userId
 */
export const tributeFlower = async (userId) => {
  if (!userId) return { success: false, message: "请先登录" };
  try {
    const response = await axiosInstance.post(
      `${API_PATHS.BASE_API_URL}${API_PATHS.SKIN.FLOWER_TRIBUTE}`,
      null,
      { params: { user_id: userId } }
    );
    return response;
  } catch (error) {
    Logger.warn("鲜花献礼失败", error);
    return { success: false, message: error.message || "献礼失败" };
  }
};

